/* eslint-disable import/no-cycle */
import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { IApiQueryOptions } from 'api/ApiClient';
import { Appearance } from 'design/types';
import { selectCurrentEmployeeType } from 'store/Employers';
import { EmployeeActionRequiredType, EmployeeStatus, EmployeeType, IEmployee } from 'types';
import { capitalize, formatDateForUI, formatInviteRecipient, formatJobTitle, getTimeWithZone } from 'utils';
import { orgRoleDisplay } from 'utils/employee';
import { isSortDesc, isSortedBy } from 'utils/table';
import { Button } from '../../Button';
import { Empty } from '../../Empty';
import { Status } from '../../Status';
import { Tooltip } from '../../Tooltip';
import { User } from '../../User';
import { ChildData } from '../ChildData';
import EmptyTable from '../EmptyTable';
import HeaderCell from '../HeaderCell';
import HeaderRow from '../HeaderRow';
import { ParentCell, ParentCellInner, ParentDataButton } from '../ParentCell';
import SortLabel from '../SortLabel';
import Table from '../Table';
import TableBody from '../TableBody';
import TableHead from '../TableHead';
import TableTimestamp from '../TableTimestamp';
import { UserTableRow } from './UserTableRow';

interface ITableProps {
  currentSiteId?: string;
  disableReviewButton?: boolean;
  employees: IEmployee[];
  tableHeaders: TableHeaderObject[];
  isLoading?: boolean;
  urlQuery: IApiQueryOptions;
  onEmployeeSelect: (employeeId: string) => void;
  onReviewSelect?: (employeeId: string) => void;
  onResendSelect?: (employeeId: string) => void;
  onSortChange: (value: string) => void;
}

export interface TableHeaderObject {
  align?: 'center' | 'left' | 'right';
  label: string;
  value: string;
  sortable?: boolean;
}

const EmployeeTable: React.FC<ITableProps> = ({ currentSiteId, employees, tableHeaders, isLoading, onEmployeeSelect, onReviewSelect, onResendSelect, onSortChange, urlQuery, disableReviewButton }) => {
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const [childVisible, setChildVisible] = useState<string>('');

  const alignTop = childVisible ? 'top' : 'center';

  // Data
  const statusDisplay = (status: string, actionRequired?: EmployeeActionRequiredType): string => {
    if (status === EmployeeStatus.ACTION_REQUIRED || status === EmployeeStatus.INVITED) {
      switch (actionRequired) {
        case EmployeeActionRequiredType.INVITE_EXPIRED:
        case EmployeeActionRequiredType.INVITE_REQUIRED:
          return 'Expired';
        case EmployeeActionRequiredType.EMPLOYER_APPROVAL:
          return 'Pending';
        case EmployeeActionRequiredType.INVITE_SEND_FAILURE:
          return 'Invited';
        default:
          break;
      }
    }
    const newStatus = status[0].toUpperCase() + status.substring(1);
    return newStatus;
  };

  const subStatusDisplay = (status: string, actionRequired?: string): string => {
    let label = '';
    if (status === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_SEND_FAILURE) {
      label = 'Delivery Failure';
    } else if ((status === EmployeeStatus.ACTION_REQUIRED || status === EmployeeStatus.INVITED) && actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
      label = 'Account Requires Approval';
    }

    return label;
  };

  const statusColor = (status: EmployeeStatus, actionRequired?: EmployeeActionRequiredType): Appearance => {
    switch (status) {
      case EmployeeStatus.APPROVED:
        return Appearance.SUCCESS;
      case EmployeeStatus.DISMISSED:
        return Appearance.NEUTRAL;
      case EmployeeStatus.ACTION_REQUIRED:
      case EmployeeStatus.INVITED:
        if ([EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_REQUIRED].includes(actionRequired)) {
          return Appearance.DANGER;
        }
        return Appearance.WARNING;
      default:
        return Appearance.WARNING;
    }
  };

  const onMultiSiteClick = (id: string, e?: MouseEvent) => {
    e?.stopPropagation();
    setChildVisible(id === childVisible ? '' : id);
  };

  const currentSort = urlQuery.sort || '';
  const isCurrentSortDesc = isSortDesc(currentSort);

  return (
    <>
      <Table scrollable sticky>
        <TableHead>
          <HeaderRow sticky>
            {tableHeaders.map(({ label, value, align, sortable }: TableHeaderObject, index: number) => (
              <HeaderCell key={index} align={align}>
                {sortable ? <SortLabel label={label} isSorted={isSortedBy(value, currentSort)} isSortedDesc={isCurrentSortDesc} onClick={() => onSortChange(value)} isLoading={isLoading} /> : isLoading ? '' : label}
              </HeaderCell>
            ))}
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading &&
            isEmpty(employees) &&
            [...Array(urlQuery.limit)].map((e, i) => {
              const displayRow = {
                name: {
                  renderComponent: (
                    <ParentCell dataLabel="Name" verticalAlign="top">
                      <ParentCellInner>
                        <Skeleton width={100} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                sites: {
                  renderComponent: (
                    <ParentCell dataLabel="Site" verticalAlign={alignTop} minWidth={150}>
                      <ParentCellInner>
                        <Skeleton width={150} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                role: {
                  renderComponent: (
                    <ParentCell dataLabel="Role" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                jobTitle: {
                  renderComponent: (
                    <ParentCell dataLabel="Job Title" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                status: {
                  renderComponent: (
                    <ParentCell dataLabel="Status" align="left" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                createdAt: {
                  renderComponent: (
                    <ParentCell dataLabel="Date Added" verticalAlign="top" minWidth={110}>
                      <ParentCellInner>
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                reviewAction: {
                  renderComponent: (
                    <ParentCell dataLabel="Review" align="center" verticalAlign="top">
                      <ParentCellInner align="right">
                        {' '}
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                inviteRecipient: {
                  renderComponent: (
                    <ParentCell dataLabel="Invite method" align="left" verticalAlign="top">
                      <ParentCellInner>
                        {' '}
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
              };

              return <UserTableRow key={i} data-cy={`user-row-${i}`} visibleColumns={tableHeaders} dataRow={displayRow} />;
            })}

          {employees?.map((employee: IEmployee, index: number) => {
            const { id: employeeId, actionRequired, inviteRecipient, sites, createdAt, type } = employee;
            const employeeStatus = currentSiteId ? employee.sites.find((site) => site.id === currentSiteId)?.status || employee.status : employee.status;
            const isOrgAdmin = type === EmployeeType.ADMIN;
            const hasEditPermission = !disableReviewButton && (!isOrgAdmin || currentEmployeeType === EmployeeType.ADMIN);
            const employeeSiteInfo = sites.reduce(
              (acc, site) => {
                if (!isOrgAdmin) {
                  acc.roles.push(orgRoleDisplay(type, site.role));
                  if (site.job) acc.jobs.push(formatJobTitle(site.job.title));
                  acc.sites.push(site.name);
                  acc.status.push(capitalize(site.status));
                }
                return acc;
              },
              { roles: [], sites: [], jobs: [], status: [] }
            );

            const displayRow = {
              name: {
                renderComponent: (
                  <ParentCell dataLabel="Name" data-cy="col-name" verticalAlign="top" onClick={() => onEmployeeSelect?.(employeeId)}>
                    <ParentCellInner>
                      <User
                        id={employee.id}
                        username={employee.name}
                        image={employee.user?.avatarUrl}
                        isLoading={isLoading}
                        stackable={false}
                        hasPaymentMethod={!!employee.user?.card}
                        isDismissed={employeeStatus === EmployeeStatus.DISMISSED}
                      />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },

              sites: {
                renderComponent: (
                  <ParentCell dataLabel="Site" data-cy="col-site" verticalAlign={alignTop} onClick={() => onEmployeeSelect?.(employeeId)} minWidth={150}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(employeeId, e)} isActive={employeeId === childVisible} data={isOrgAdmin ? ['All'] : employeeSiteInfo.sites} label="Sites" isLoading={isLoading} />
                    </ParentCellInner>

                    <ChildData position="start" isVisible={employeeId === childVisible} data={employeeSiteInfo.sites.length === 1 ? null : employeeSiteInfo.sites} />
                  </ParentCell>
                ),
              },

              role: {
                renderComponent: (
                  <ParentCell dataLabel="Role" data-cy="col-role" verticalAlign="top" onClick={() => onEmployeeSelect?.(employeeId)} minWidth={121}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(employeeId, e)} isActive={employeeId === childVisible} data={isOrgAdmin ? [orgRoleDisplay(type)] : employeeSiteInfo.roles} label="Roles" isLoading={isLoading} />
                    </ParentCellInner>

                    <ChildData position="middle" isVisible={employeeId === childVisible} data={employeeSiteInfo.roles.length === 1 ? null : employeeSiteInfo.roles} />
                  </ParentCell>
                ),
              },

              jobTitle: {
                renderComponent: (
                  <ParentCell dataLabel="Job Title" data-cy="col-job-title" verticalAlign="top" onClick={() => onEmployeeSelect?.(employeeId)} minWidth={121}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(employeeId, e)} isActive={employeeId === childVisible} data={employeeSiteInfo.jobs} label="Jobs" isLoading={isLoading} />
                    </ParentCellInner>
                    <ChildData position="middle" isVisible={employeeId === childVisible} data={employeeSiteInfo.jobs.length === 1 ? null : employeeSiteInfo.jobs} />
                  </ParentCell>
                ),
              },

              status: {
                renderComponent: (
                  <ParentCell dataLabel="Status" data-cy="col-status" align="left" verticalAlign="top" minWidth={121} onClick={() => onEmployeeSelect?.(employeeId)}>
                    <ParentCellInner>
                      <Status appearance={statusColor(employeeStatus, actionRequired)} label={statusDisplay(employeeStatus, actionRequired)} subStatusLabel={subStatusDisplay(employeeStatus, actionRequired)} isLoading={isLoading} />
                    </ParentCellInner>
                    <ChildData
                      position="end"
                      isVisible={employee.id === childVisible}
                      data={
                        employeeSiteInfo.status.length === 1 ? null : employeeStatus === EmployeeStatus.ACTION_REQUIRED ? Array(employeeSiteInfo.status.length).fill(statusDisplay(employeeStatus, actionRequired)) : employeeSiteInfo.status
                      }
                    />
                  </ParentCell>
                ),
              },
              createdAt: {
                renderComponent: (
                  <ParentCell dataLabel="Date Added" verticalAlign="top" onClick={() => onEmployeeSelect?.(employeeId)} minWidth={110}>
                    <ParentCellInner>
                      <TableTimestamp isLoading={isLoading} date={formatDateForUI(createdAt)} time={getTimeWithZone(createdAt)} />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },

              reviewAction: {
                renderComponent: (
                  <ParentCell dataLabel="Review" align="center" verticalAlign="top">
                    {[EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_SEND_FAILURE, EmployeeActionRequiredType.INVITE_REQUIRED].includes(actionRequired) && hasEditPermission && (
                      <ParentCellInner align="right">
                        <Button
                          appearance="white"
                          size="small"
                          label="Resend"
                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            e?.stopPropagation();
                            onResendSelect?.(employeeId);
                          }}
                          isLoading={isLoading}
                          disabled={isLoading}
                        />
                      </ParentCellInner>
                    )}

                    {actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL && (
                      <ParentCellInner align="right">
                        <Button
                          appearance="primary"
                          size="small"
                          label="Review"
                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            e?.stopPropagation();
                            onReviewSelect?.(employeeId);
                          }}
                          isLoading={isLoading}
                          disabled={isLoading || !hasEditPermission}
                          tooltipId={`${employeeId}-manager-disabled`}
                        />
                        {!hasEditPermission && (
                          <Tooltip id={`${employeeId}-manager-disabled`} place="left">
                            You do not have permission to review this user.
                            <br />
                            Contact your manager.
                          </Tooltip>
                        )}
                      </ParentCellInner>
                    )}
                  </ParentCell>
                ),
              },

              inviteRecipient: {
                renderComponent: (
                  <ParentCell dataLabel="Invite method" align="left" verticalAlign="top" onClick={() => onEmployeeSelect?.(employeeId)}>
                    <ParentCellInner>
                      <ParentDataButton data={[inviteRecipient ? formatInviteRecipient(inviteRecipient) : '']} label="Invite Recipient" onClick={() => onEmployeeSelect?.(employeeId)} isLoading={isLoading} />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },
            };

            return <UserTableRow key={index} data-cy={`user-row-${index}`} visibleColumns={tableHeaders} dataRow={displayRow} />;
          })}

          {!isLoading && isEmpty(employees) && (
            <EmptyTable>
              <Empty icon="People" message="No users found." />
            </EmptyTable>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default EmployeeTable;
